/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback } from 'react';
import { db } from '@db/database';
import { useDownloadStore } from '@stores/downloadStore';
import { useUserStore } from '@stores/userStore';
import { enterpriseApiClient } from '@api/enterprise/client';
import {
  WorkOrdersApi,
  EquipmentApi,
  SpecificationsApi,
  IndyODataApi
} from '@api/enterprise/openapi';
import { GlobalVariablesApi } from '@api/enterprise/openapi/endpoints/global-variables-api';
import { DcRuntimeApi } from '@api/bff/openapi/endpoints/dc-runtime-api';
import { EnterpriseSyncApi } from '@api/bff/openapi/endpoints/enterprise-sync-api';
import { bffApiClient } from '@api/bff/client';
import type {
  WorkOrderViewModel,
  WorkOrderDetailViewModel,
  EquipmentViewModel,
  GlobalVariableViewModel,
  SpecificationViewModel,
  WorkOrderChargeViewModel,
  RepairPartViewModel,
  ServiceAgreementChargeTypeViewModel
} from '@api/enterprise/openapi';
import type {
  DcRuntimeViewModel,
  EventDocumentViewModel
} from '@api/bff/openapi/models';

export const useOfflineDownloadService = () => {
  const setDownloadStatus = useDownloadStore(state => state.setDownloadStatus);
  const setDownloadProgress = useDownloadStore(
    state => state.setDownloadProgress
  );
  const addToQueue = useDownloadStore(state => state.addToQueue);
  const removeFromQueue = useDownloadStore(state => state.removeFromQueue);
  const userName = useUserStore(state => state.userName) ?? '';

  const calculateDataSize = (
    workOrder: WorkOrderViewModel,
    details: WorkOrderDetailViewModel[],
    equipment: EquipmentViewModel[] = [],
    specifications: SpecificationViewModel[] = []
  ): number => {
    const workOrderSize = JSON.stringify(workOrder).length;
    const detailsSize = JSON.stringify(details).length;
    const equipmentSize = JSON.stringify(equipment).length;
    const specificationsSize = JSON.stringify(specifications).length;
    return workOrderSize + detailsSize + equipmentSize + specificationsSize;
  };

  const downloadWorkOrder = useCallback(
    async (jobNumber: string) => {
      try {
        // Clear any existing data first to ensure clean download
        await db.transaction(
          'rw',
          [
            db.cachedWorkOrders,
            db.cachedWorkOrderDetails,
            db.cachedCharges,
            db.cachedCalibrationRuntimes,
            db.cachedEventDocuments,
            db.downloadMetadata
          ],
          async () => {
            const jobDetails = await db.cachedWorkOrderDetails
              .where('jobNumber')
              .equals(jobNumber)
              .toArray();
            const globalIds = jobDetails
              .map(d => d.globalId)
              .filter((id): id is string => !!id);

            await db.cachedWorkOrders
              .where('jobNumber')
              .equals(jobNumber)
              .delete();
            if (jobDetails.length > 0) {
              await db.cachedWorkOrderDetails
                .where('jobNumber')
                .equals(jobNumber)
                .delete();
            }
            await db.cachedCharges
              .where('jobNumber')
              .equals(jobNumber)
              .delete();
            if (globalIds.length > 0) {
              await db.cachedCalibrationRuntimes
                .where('woDetailGlobalId')
                .anyOf(globalIds)
                .delete();
            }
            await db.cachedEventDocuments
              .where('eventNumber')
              .above(0)
              .delete();
            await db.downloadMetadata
              .where('jobNumber')
              .equals(jobNumber)
              .delete();
          }
        );

        setDownloadStatus(jobNumber, 'downloading');
        setDownloadProgress(jobNumber, 0);
        addToQueue(jobNumber);

        // Download critical global variables FIRST and ensure they succeed
        try {
          const globalVariablesApi = new GlobalVariablesApi(
            undefined,
            undefined,
            enterpriseApiClient
          );

          const now = new Date().toISOString();

          // Download PreCal checklist
          const checklistResponse =
            await globalVariablesApi.apiGlobalVariablesNameGet({
              name: 'HIDE_PRECAL_CHECKLIST'
            });

          if (checklistResponse.data) {
            // Save immediately to ensure it's cached
            await db.cachedGlobalVariables.put({
              ...checklistResponse.data,
              name: 'HIDE_PRECAL_CHECKLIST',
              cachedAt: now,
              version: 1
            });
          } else {
            console.warn('No PreCal checklist data returned from API');
          }

          // Download allowed events
          const allowedEventsResponse =
            await globalVariablesApi.apiGlobalVariablesNameGet({
              name: 'HIDE_ALLOWED_EVENT_TYPES'
            });

          if (allowedEventsResponse.data) {
            // Save immediately to ensure it's cached
            await db.cachedGlobalVariables.put({
              ...allowedEventsResponse.data,
              name: 'HIDE_ALLOWED_EVENT_TYPES',
              cachedAt: now,
              version: 1
            });
          } else {
            console.warn('No allowed events data returned from API');
          }

          // Download equipment assessment conditions
          const assessmentResponse =
            await globalVariablesApi.apiGlobalVariablesNameGet({
              name: 'HIDE_ASSESSMENT_CONDITIONS'
            });

          if (assessmentResponse.data) {
            await db.cachedGlobalVariables.put({
              ...assessmentResponse.data,
              name: 'HIDE_ASSESSMENT_CONDITIONS',
              cachedAt: now,
              version: 1
            });
          } else {
            console.warn('No assessment conditions data returned from API');
          }

          // Download customer acknowledgment statement
          const acknowledgmentResponse =
            await globalVariablesApi.apiGlobalVariablesNameGet({
              name: 'HIDE_CUSTOMER_ACKNOWLEDGMENT'
            });

          if (acknowledgmentResponse.data) {
            await db.cachedGlobalVariables.put({
              ...acknowledgmentResponse.data,
              name: 'HIDE_CUSTOMER_ACKNOWLEDGMENT',
              cachedAt: now,
              version: 1
            });
          } else {
            console.warn('No customer acknowledgment data returned from API');
          }

          // Download certificate template configuration
          try {
            const certTemplateResponse =
              await globalVariablesApi.apiGlobalVariablesNameGet({
                name: 'HIDE_CERT_TEMPLATE'
              });

            if (certTemplateResponse.data) {
              await db.cachedGlobalVariables.put({
                ...certTemplateResponse.data,
                name: 'HIDE_CERT_TEMPLATE',
                cachedAt: now,
                version: 1
              });
            }
          } catch (error) {
            console.warn('Failed to cache certificate template config:', error);
          }
        } catch (error) {
          console.error('Failed to download global variables:', error);
          // Continue with work order download even if checklist fails
        }

        setDownloadProgress(jobNumber, 5);

        try {
          const indyODataApi = new IndyODataApi(
            undefined,
            undefined,
            enterpriseApiClient
          );

          const now = new Date().toISOString();

          const partsCount = await db.cachedRepairParts.count();
          if (partsCount === 0) {
            const partsResponse = await indyODataApi.apiOdataRepairPartsGet({
              $select: 'partNumber,description,companyName,cost,partName'
            });

            if (
              partsResponse.data?.value &&
              partsResponse.data.value.length > 0
            ) {
              const partsToCache = partsResponse.data.value.map(part => ({
                ...part,
                cachedAt: now
              }));
              await db.cachedRepairParts.bulkPut(partsToCache);
            }
          }

          const chargeTypesCount = await db.cachedChargeTypes.count();
          if (chargeTypesCount === 0) {
            const chargeTypesResponse =
              await indyODataApi.apiOdataServiceAgreementChargeTypesGet({});

            if (
              chargeTypesResponse.data?.value &&
              chargeTypesResponse.data.value.length > 0
            ) {
              const chargeTypesToCache = chargeTypesResponse.data.value.map(
                ct => ({
                  ...ct,
                  cachedAt: now
                })
              );
              await db.cachedChargeTypes.bulkPut(chargeTypesToCache);
            }
          }
        } catch (error) {
          console.warn('Failed to cache reference data:', error);
        }

        const workOrdersApi = new WorkOrdersApi(
          undefined,
          undefined,
          enterpriseApiClient
        );

        setDownloadProgress(jobNumber, 15);

        const workOrderResponse = await workOrdersApi.apiWorkOrdersJobNumberGet(
          {
            jobNumber
          }
        );

        if (!workOrderResponse.data) {
          throw new Error('Failed to fetch work order');
        }

        setDownloadProgress(jobNumber, 45);

        const detailsResponse =
          await workOrdersApi.apiWorkOrdersJobNumberDetailsGet({
            jobNumber
          });

        const details = detailsResponse.data || [];

        setDownloadProgress(jobNumber, 50);

        // Fetch equipment data for each detail that has companyName and id
        const equipmentApi = new EquipmentApi(
          undefined,
          undefined,
          enterpriseApiClient
        );

        const equipmentData: EquipmentViewModel[] = [];
        const detailsWithEquipment = details.filter(
          detail => detail.companyName && detail.id
        );

        if (detailsWithEquipment.length > 0) {
          const equipmentProgressStep = 20 / detailsWithEquipment.length; // 20% for equipment
          let currentProgress = 50;

          for (const detail of detailsWithEquipment) {
            try {
              // Sanitize id - Enterprise API can return ids with embedded CRLF instead of spaces
              const sanitizedId = detail.id!.replace(/[\r\n]+/g, ' ').trim();
              const equipmentResponse =
                await equipmentApi.apiEquipmentCompanyNameIdGet({
                  companyName: detail.companyName!,
                  id: sanitizedId
                });

              if (equipmentResponse.data) {
                equipmentData.push(equipmentResponse.data);
              }
            } catch (error) {
              // Continue even if some equipment data fails to fetch
              console.warn(
                `Failed to fetch equipment for ${detail.companyName}/${detail.id}:`,
                error
              );
            }

            currentProgress += equipmentProgressStep;
            setDownloadProgress(jobNumber, Math.round(currentProgress));
          }
        }

        setDownloadProgress(jobNumber, 70);

        // Fetch specifications for each equipment
        const specificationsApi = new SpecificationsApi(
          undefined,
          undefined,
          enterpriseApiClient
        );

        const allSpecifications: SpecificationViewModel[] = [];

        if (equipmentData.length > 0) {
          const specificationsProgressStep = 10 / equipmentData.length; // 10% for specifications
          let currentProgress = 70;

          for (const equipment of equipmentData) {
            try {
              const specificationsResponse =
                await specificationsApi.apiEquipmentCompanyNameIdSpecificationsGet(
                  {
                    companyName: equipment.companyName!,
                    id: equipment.id!
                  }
                );

              if (
                specificationsResponse.data &&
                specificationsResponse.data.length > 0
              ) {
                allSpecifications.push(...specificationsResponse.data);
              }
            } catch (error) {
              // Continue even if some specifications data fails to fetch
              console.warn(
                `Failed to fetch specifications for ${equipment.companyName}/${equipment.id}:`,
                error
              );
            }

            currentProgress += specificationsProgressStep;
            setDownloadProgress(jobNumber, Math.round(currentProgress));
          }
        }

        setDownloadProgress(jobNumber, 80);

        // Fetch charges (includes both labor/misc charges AND parts)
        let chargesData: WorkOrderChargeViewModel[] = [];
        try {
          const chargesResponse =
            await workOrdersApi.apiWorkOrdersJobNumberChargesGet({
              jobNumber
            });

          if (chargesResponse.data && chargesResponse.data.length > 0) {
            chargesData = chargesResponse.data;
          }
        } catch (error) {
          console.warn(`Failed to fetch charges for ${jobNumber}:`, error);
        }

        setDownloadProgress(jobNumber, 85);

        // Fetch calibration runtimes for each detail
        const dcRuntimeApi = new DcRuntimeApi(
          undefined,
          undefined,
          bffApiClient
        );

        const calibrationRuntimes: DcRuntimeViewModel[] = [];

        if (details.length > 0) {
          const calibrationProgressStep = 10 / details.length;
          let currentProgress = 85;

          for (const detail of details) {
            if (detail.globalId) {
              try {
                const response =
                  await dcRuntimeApi.apiDcRuntimeByWoDetailWoDetailGlobalIdGet({
                    woDetailGlobalId: detail.globalId
                  });

                if (response.data) {
                  calibrationRuntimes.push(response.data);
                }
              } catch (error: unknown) {
                if (error && typeof error === 'object' && 'response' in error) {
                  const axiosError = error as {
                    response?: { status?: number };
                  };
                  if (axiosError.response?.status !== 204) {
                    console.warn(
                      `Failed to fetch calibration runtime for ${detail.globalId}:`,
                      error
                    );
                  }
                }
              }
            }

            currentProgress += calibrationProgressStep;
            setDownloadProgress(jobNumber, Math.round(currentProgress));
          }
        }

        setDownloadProgress(jobNumber, 92);

        // Fetch event documents for calibrations that have been synced (have eventNum)
        const enterpriseSyncApi = new EnterpriseSyncApi(
          undefined,
          undefined,
          bffApiClient
        );

        const eventDocuments: EventDocumentViewModel[] = [];
        const eventNumbers = new Set<number>();

        for (const calibration of calibrationRuntimes) {
          if (calibration.eventNum && calibration.eventNum > 0) {
            eventNumbers.add(calibration.eventNum);
          }
        }

        if (eventNumbers.size > 0) {
          for (const eventNumber of eventNumbers) {
            try {
              const response =
                await enterpriseSyncApi.apiEnterpriseSyncEventDocumentsEventNumberGet(
                  { eventNumber }
                );

              if (response.data && response.data.length > 0) {
                eventDocuments.push(...response.data);
              }
            } catch (error) {
              console.warn(
                `Failed to fetch event documents for event ${eventNumber}:`,
                error
              );
            }
          }
        }

        setDownloadProgress(jobNumber, 95);

        const now = new Date().toISOString();
        const version = 1;
        const size = calculateDataSize(
          workOrderResponse.data,
          details,
          equipmentData,
          allSpecifications
        );

        await db.transaction(
          'rw',
          [
            db.cachedWorkOrders,
            db.cachedWorkOrderDetails,
            db.cachedEquipment,
            db.cachedCharges,
            db.cachedCalibrationRuntimes,
            db.cachedEventDocuments,
            db.downloadMetadata
          ],
          async () => {
            await db.cachedWorkOrders.put({
              ...workOrderResponse.data,
              jobNumber: String(jobNumber), // Ensure jobNumber is stored as string for consistency
              cachedAt: now,
              version,
              _userId: userName
            });

            if (details.length > 0) {
              // Use individual puts instead of bulkPut to ensure each item is saved
              for (const detail of details) {
                // Remove the id field from the API response to avoid conflicts with auto-increment
                const { id, ...detailWithoutId } = detail;

                const detailToCache = {
                  ...detailWithoutId,
                  originalId: id, // Store original id with different name
                  jobNumber: String(jobNumber), // Ensure jobNumber is stored as string for consistency
                  cachedAt: now,
                  _userId: userName
                };

                try {
                  await db.cachedWorkOrderDetails.add(detailToCache);
                } catch (err) {
                  // Try put instead of add if it already exists
                  await db.cachedWorkOrderDetails.put(detailToCache);
                }
              }
            }

            if (equipmentData.length > 0) {
              for (const equipment of equipmentData) {
                const equipmentToCache = {
                  ...equipment,
                  cachedAt: now,
                  _userId: userName
                };

                try {
                  await db.cachedEquipment.put(equipmentToCache);
                } catch (err) {
                  console.warn(
                    `Failed to cache equipment ${equipment.companyName}/${equipment.id}:`,
                    err
                  );
                }
              }
            }

            if (chargesData.length > 0) {
              for (const charge of chargesData) {
                const chargeToCache = {
                  ...charge,
                  jobNumber: String(jobNumber),
                  cachedAt: now,
                  _userId: userName
                };

                try {
                  await db.cachedCharges.add(chargeToCache);
                } catch (err) {
                  await db.cachedCharges.put(chargeToCache);
                }
              }
            }

            if (calibrationRuntimes.length > 0) {
              for (const calibration of calibrationRuntimes) {
                const calibrationToCache = {
                  ...calibration,
                  cachedAt: now,
                  _userId: userName
                };

                try {
                  await db.cachedCalibrationRuntimes.add(calibrationToCache);
                } catch (err) {
                  await db.cachedCalibrationRuntimes.put(calibrationToCache);
                }
              }
            }

            if (eventDocuments.length > 0) {
              for (const eventDoc of eventDocuments) {
                const eventDocToCache = {
                  ...eventDoc,
                  cachedAt: now,
                  _userId: userName
                };

                try {
                  await db.cachedEventDocuments.add(eventDocToCache);
                } catch (err) {
                  await db.cachedEventDocuments.put(eventDocToCache);
                }
              }
            }

            await db.downloadMetadata.put({
              jobNumber: String(jobNumber), // Ensure jobNumber is stored as string for consistency
              status: 'cached',
              downloadedAt: now,
              lastSyncedAt: now,
              version,
              size,
              _userId: userName
            });
          }
        );

        // Cache specifications in a separate transaction
        if (allSpecifications.length > 0) {
          // Group specifications by equipment
          const specsByEquipment = new Map<string, SpecificationViewModel[]>();

          for (const spec of allSpecifications) {
            if (spec.equipmentId && spec.companyName) {
              const key = `${spec.companyName}_${spec.equipmentId}`;
              if (!specsByEquipment.has(key)) {
                specsByEquipment.set(key, []);
              }
              specsByEquipment.get(key)!.push(spec);
            }
          }

          // Store specifications for each equipment
          await db.transaction('rw', db.cachedSpecifications, async () => {
            for (const [key, specs] of specsByEquipment) {
              const [companyName, equipmentId] = key.split('_');
              for (const specification of specs) {
                const specToCache = {
                  ...specification,
                  companyName,
                  equipmentId,
                  cachedAt: now,
                  _userId: userName
                };

                try {
                  await db.cachedSpecifications.add(specToCache);
                } catch (err) {
                  // Try put instead of add if it already exists
                  try {
                    await db.cachedSpecifications.put(specToCache);
                  } catch (putErr) {
                    console.warn(
                      `Failed to cache specification for ${companyName}/${equipmentId}:`,
                      putErr
                    );
                  }
                }
              }
            }
          });
        }

        setDownloadProgress(jobNumber, 100);
        setDownloadStatus(jobNumber, 'cached');
        removeFromQueue(jobNumber);

        await useDownloadStore.getState().updateStorageStats();

        return true;
      } catch (error) {
        setDownloadStatus(jobNumber, 'error');
        removeFromQueue(jobNumber);

        await db.downloadMetadata.put({
          jobNumber: String(jobNumber), // Ensure jobNumber is stored as string for consistency
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
          version: 0,
          size: 0,
          _userId: userName
        });

        return false;
      }
    },
    [
      setDownloadStatus,
      setDownloadProgress,
      addToQueue,
      removeFromQueue,
      userName
    ]
  );

  const getCachedWorkOrder = useCallback(
    async (jobNumber: string): Promise<WorkOrderViewModel | null> => {
      try {
        const jobNumberStr = String(jobNumber);
        const jobNumberNum = Number(jobNumber);

        // Try string first (our consistent storage format)
        let cached = await db.cachedWorkOrders
          .where('jobNumber')
          .equals(jobNumberStr)
          .and(item => item._userId === userName)
          .first();

        // Fallback to number for backward compatibility
        if (!cached && !isNaN(jobNumberNum)) {
          cached = await db.cachedWorkOrders
            .where('jobNumber')
            .equals(jobNumberNum)
            .and(item => item._userId === userName)
            .first();
        }

        return cached ?? null;
      } catch (error) {
        return null;
      }
    },
    [userName]
  );

  const getCachedWorkOrderDetails = useCallback(
    async (jobNumber: string): Promise<WorkOrderDetailViewModel[]> => {
      try {
        const jobNumberStr = String(jobNumber);
        const jobNumberNum = Number(jobNumber);

        // Try string first (our consistent storage format)
        let cached = await db.cachedWorkOrderDetails
          .where('jobNumber')
          .equals(jobNumberStr)
          .and(item => item._userId === userName)
          .toArray();

        // Fallback to number for backward compatibility
        if (cached.length === 0 && !isNaN(jobNumberNum)) {
          cached = await db.cachedWorkOrderDetails
            .where('jobNumber')
            .equals(jobNumberNum)
            .and(item => item._userId === userName)
            .toArray();
        }

        if (cached && cached.length > 0) {
          // Restore the original id field
          return cached.map(item => {
            const { originalId, ...rest } = item;
            return {
              ...rest,
              id: originalId
            } as WorkOrderDetailViewModel;
          });
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    [userName]
  );

  const getCachedEquipment = useCallback(
    async (
      companyName: string,
      id: string
    ): Promise<EquipmentViewModel | null> => {
      try {
        const cached = await db.cachedEquipment
          .where('[companyName+id]')
          .equals([companyName, id])
          .and(item => item._userId === userName)
          .first();

        return cached || null;
      } catch (error) {
        return null;
      }
    },
    [userName]
  );

  const getCachedSpecifications = useCallback(
    async (
      companyName: string,
      equipmentId: string
    ): Promise<SpecificationViewModel[]> => {
      try {
        const cached = await db.cachedSpecifications
          .where('[companyName+equipmentId]')
          .equals([companyName, equipmentId])
          .and(item => item._userId === userName)
          .toArray();

        return cached || [];
      } catch (error) {
        console.warn(
          `Failed to get cached specifications for ${companyName}/${equipmentId}:`,
          error
        );
        return [];
      }
    },
    [userName]
  );

  const getCachedCharges = useCallback(
    async (jobNumber: string): Promise<WorkOrderChargeViewModel[]> => {
      try {
        const jobNumberStr = String(jobNumber);

        const cached = await db.cachedCharges
          .where('jobNumber')
          .equals(jobNumberStr)
          .and(item => item._userId === userName)
          .toArray();

        return cached || [];
      } catch (error) {
        console.warn(`Failed to get cached charges for ${jobNumber}:`, error);
        return [];
      }
    },
    [userName]
  );

  const getCachedCalibrationRuntime = useCallback(
    async (woDetailGlobalId: string): Promise<DcRuntimeViewModel | null> => {
      try {
        const cached = await db.cachedCalibrationRuntimes
          .where('woDetailGlobalId')
          .equals(woDetailGlobalId)
          .and(item => item._userId === userName)
          .first();

        return cached || null;
      } catch (error) {
        console.warn(
          `Failed to get cached calibration runtime for woDetailGlobalId ${woDetailGlobalId}:`,
          error
        );
        return null;
      }
    },
    [userName]
  );

  const getCachedEventDocuments = useCallback(
    async (eventNumber: number): Promise<EventDocumentViewModel[]> => {
      try {
        const cached = await db.cachedEventDocuments
          .where('eventNumber')
          .equals(eventNumber)
          .and(item => item._userId === userName)
          .toArray();

        return cached || [];
      } catch (error) {
        console.warn(
          `Failed to get cached event documents for event ${eventNumber}:`,
          error
        );
        return [];
      }
    },
    [userName]
  );

  const getCachedRepairParts = useCallback(async (): Promise<
    RepairPartViewModel[]
  > => {
    try {
      return await db.cachedRepairParts.toArray();
    } catch (error) {
      console.warn('Failed to get cached repair parts:', error);
      return [];
    }
  }, []);

  const getCachedChargeTypes = useCallback(async (): Promise<
    ServiceAgreementChargeTypeViewModel[]
  > => {
    try {
      return await db.cachedChargeTypes.toArray();
    } catch (error) {
      console.warn('Failed to get cached charge types:', error);
      return [];
    }
  }, []);

  const checkForUpdates = useCallback(
    async (jobNumber: string): Promise<boolean> => {
      try {
        const metadata = await db.downloadMetadata.get(jobNumber);
        if (!metadata || metadata.status !== 'cached') {
          return false;
        }

        const workOrdersApi = new WorkOrdersApi(
          undefined,
          undefined,
          enterpriseApiClient
        );

        const workOrderResponse = await workOrdersApi.apiWorkOrdersJobNumberGet(
          {
            jobNumber
          }
        );

        if (!workOrderResponse.data) {
          return false;
        }

        const cachedWorkOrder = await db.cachedWorkOrders.get(jobNumber);
        if (!cachedWorkOrder) {
          return false;
        }

        const hasChanges =
          JSON.stringify(workOrderResponse.data) !==
          JSON.stringify({
            ...cachedWorkOrder,
            cachedAt: undefined,
            version: undefined,
            lastModified: undefined
          });

        if (hasChanges) {
          await db.downloadMetadata.update(jobNumber, {
            status: 'update_available'
          });
          setDownloadStatus(jobNumber, 'update_available');
          return true;
        }

        return false;
      } catch (error) {
        return false;
      }
    },
    [setDownloadStatus]
  );

  const getCachedGlobalVariable = useCallback(
    async (name: string): Promise<GlobalVariableViewModel | null> => {
      try {
        const cached = await db.cachedGlobalVariables.get(name);
        return cached || null;
      } catch (error) {
        return null;
      }
    },
    []
  );

  const syncOfflineChanges = useCallback(async (): Promise<void> => {
    try {
      const syncQueue = await db.syncQueue.toArray();

      for (const item of syncQueue) {
        try {
          await db.syncQueue.delete(item.id!);
        } catch (error) {
          if (item.retryCount && item.retryCount >= 3) {
            await db.syncQueue.delete(item.id!);
          } else {
            await db.syncQueue.update(item.id!, {
              retryCount: (item.retryCount || 0) + 1
            });
          }
        }
      }
    } catch {
      // Silently handle errors during service initialization
    }
  }, []);

  return {
    downloadWorkOrder,
    getCachedWorkOrder,
    getCachedWorkOrderDetails,
    getCachedEquipment,
    getCachedSpecifications,
    getCachedCharges,
    getCachedCalibrationRuntime,
    getCachedEventDocuments,
    getCachedGlobalVariable,
    getCachedRepairParts,
    getCachedChargeTypes,
    checkForUpdates,
    syncOfflineChanges
  };
};
